import { Button, FlexBox, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';

import { aiApi } from 'apis/ai';
import { appActions } from 'app/app.actions';
import { TranslationBadgeDropdownContentProps } from 'components/translationBadge/translationBadge.types';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/** Represents the component for displaying the translate badge dropdown content. */
export const DropdownContent = (
  props: TranslationBadgeDropdownContentProps,
): JSX.Element => {
  const { feedbackId, onAdditionalFeedback } = props;
  const dispatch = useAppDispatch();

  const [feedback, setFeedback] = useState<'good' | 'bad'>();

  const handleThumbsUp = useCallback(() => {
    setFeedback('good');
  }, []);

  const handleThumbsDown = useCallback(() => {
    setFeedback('bad');
  }, []);

  const handleAdditionalFeedback = useCallback(async () => {
    if (!feedback) return;

    onAdditionalFeedback?.();

    const { isSent, feedbackMessage } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Translation feedback',
            team: 'conversations',
            placeholder:
              feedback === 'good'
                ? 'What do you like about the translation?'
                : 'What was the issue with the translation? How could it be improved?',
            extra: {
              feedback,
              feedbackId,
              feature: 'conversation_translate',
            },
          },
        },
      }),
    );

    if (isSent && feedbackMessage) {
      dispatch(
        aiApi.endpoints.createAiFeedback.initiate({
          feedbackId,
          feedback: [
            {
              type: 'rating',
              value: feedback,
            },
            {
              type: 'comment',
              value: feedbackMessage,
            },
          ],
        }),
      );
    }
  }, [dispatch, feedback, feedbackId, onAdditionalFeedback]);

  const actions = useMemo(() => {
    return (
      <FlexBox gap="200" justifyContent="center">
        {feedback ? (
          <Button variant="secondary" onClick={handleAdditionalFeedback}>
            Tell us more
          </Button>
        ) : (
          <>
            <Tooltip content="This translation is not good">
              <IconButton
                onClick={handleThumbsDown}
                variant="secondary"
                icon="thumbs-down"
              />
            </Tooltip>

            <Tooltip content="This translation is good">
              <IconButton
                onClick={handleThumbsUp}
                variant="secondary"
                icon="thumbs-up"
              />
            </Tooltip>
          </>
        )}
      </FlexBox>
    );
  }, [feedback, handleAdditionalFeedback, handleThumbsDown, handleThumbsUp]);

  return (
    <FlexBox flexDirection="column" gap="300" padding="300">
      {feedback
        ? 'Thanks for the feedback!'
        : 'How would you review this translation.'}

      {actions}
    </FlexBox>
  );
};
